import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('fendanacor_token'));
    const [loading, setLoading] = useState(true);

    const setAuthToken = useCallback((newToken) => {
        if (newToken) {
            localStorage.setItem('fendanacor_token', newToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        } else {
            localStorage.removeItem('fendanacor_token');
            delete axios.defaults.headers.common['Authorization'];
        }
        setToken(newToken);
    }, []);

    const fetchUser = useCallback(async () => {
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            const response = await axios.get(`${API}/auth/me`);
            setUser(response.data);
        } catch (error) {
            console.error('Failed to fetch user:', error);
            setAuthToken(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, [token, setAuthToken]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    const login = async (email, password, mfaCode = null) => {
        const payload = { email, password };
        if (mfaCode) payload.mfa_code = mfaCode;
        const response = await axios.post(`${API}/auth/login`, payload);
        const { access_token, user: userData, requires_subscription } = response.data;
        setAuthToken(access_token);
        setUser(userData);
        return { ...userData, requires_subscription };
    };

    const register = async (userData) => {
        const response = await axios.post(`${API}/auth/register`, userData);
        const { access_token, user: newUser, requires_subscription } = response.data;
        setAuthToken(access_token);
        setUser(newUser);
        return { ...newUser, requires_subscription };
    };

    const logout = () => {
        setAuthToken(null);
        setUser(null);
    };

    const value = {
        user,
        token,
        authToken: token,  // Alias for backward compatibility
        loading,
        login,
        register,
        logout,
        isAuthenticated: !!user,
        isDoctor: user?.role === 'doctor',
        isConsultant: user?.role === 'consultant',
        isAdmin: user?.role === 'admin'
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
